import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  IconButton,
  OutlinedInput,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TeacherFormData, useTeacher } from "../../hooks/useTeacher";
import CommonModal from "../common/CommonModal";
import { SelectChangeEvent } from "@mui/material/Select";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useClasses } from "../../hooks/useClasses";


interface TeacherFormModalProps {
  open: boolean;
  handleClose: () => void;
  initialData?: TeacherFormData & { id?: number };
  onSubmit: (data: TeacherFormData & { sessionId?: number }) => void;
  isEdit: boolean;
  schoolId: number;
}

const TeacherFormModal: React.FC<TeacherFormModalProps> = ({
  open,
  handleClose,
  initialData,
  onSubmit,
  isEdit,
  schoolId,
}) => {
  const { user } = useAuth();
  const { classesQuery } = useClasses(schoolId);
  const { t } = useTranslation();
  const { selectedSession, selectedClass } = useSessionClass();
  const allClasses = classesQuery.data || [];

  // Show all available classes (no filtering - teachers can be assigned to any class)
  const availableClasses = React.useMemo(() => {
    return allClasses.map((classItem) => {
      const displayName = classItem.section 
        ? `${classItem.name} ${classItem.section}` 
        : classItem.name;
      return {
        ...classItem,
        displayName,
      };
    });
  }, [allClasses]);

  const sortedAvailableClasses = React.useMemo(() => {
    const normalize = (v: unknown) =>
      String(v ?? "")
        .trim()
        .replace(/\s+/g, " ");

    const parseClassSortKey = (displayNameRaw: unknown) => {
      const displayName = normalize(displayNameRaw);
      const s = displayName.toLowerCase();

      // Split into base + "section" (last token), so: "nursery a", "1st b", "lkg 1"
      const tokens = s.split(" ").filter(Boolean);
      const lastToken = tokens.length ? tokens[tokens.length - 1] : "";
      const baseTokens = tokens.length > 1 ? tokens.slice(0, -1) : tokens;
      const base = baseTokens.join(" ");
      const sectionToken = tokens.length > 1 ? lastToken : "";

      // levelRank: smaller comes first
      // pre-nursery < nursery < lkg < ukg < grade(1..)
      let levelRank = 99;
      let grade = 0;

      const baseNoHyphen = base.replace(/-/g, " ");
      const baseCompact = baseNoHyphen.replace(/\s+/g, " ").trim();

      const isPreNursery = /^(pre\s*nursery|prenursery)$/i.test(baseCompact) || /pre\s*nursery/.test(s);
      const isNursery = /^nursery$/i.test(baseCompact) || baseCompact.startsWith("nursery ");
      const isLkg = /^lkg$/i.test(baseCompact) || baseCompact.startsWith("lkg ");
      const isUkg = /^ukg$/i.test(baseCompact) || baseCompact.startsWith("ukg ");

      if (isPreNursery) {
        levelRank = 0;
      } else if (isNursery) {
        levelRank = 1;
      } else if (isLkg) {
        levelRank = 2;
      } else if (isUkg) {
        levelRank = 3;
      } else {
        // Grades like "1", "1st", "2nd", "10th", etc.
        // Also handle strings where base itself is the grade (e.g., "1st")
        const gradeMatch =
          baseCompact.match(/^(\d+)(st|nd|rd|th)?$/i) ||
          baseCompact.match(/^(\d+)(st|nd|rd|th)\b/i) ||
          s.match(/\b(\d+)(st|nd|rd|th)?\b/i);

        if (gradeMatch?.[1]) {
          levelRank = 10;
          grade = Number(gradeMatch[1]) || 0;
        } else {
          // Unknown formats go to end but stable
          levelRank = 99;
        }
      }

      // Section sorting: no section first, then numeric (1,2,3...), then alpha (A,B,C...)
      let sectionType = 0; // 0 none, 1 numeric, 2 alpha, 3 other
      let sectionValue = 0;
      const section = normalize(sectionToken).toLowerCase();
      if (!section) {
        sectionType = 0;
      } else if (/^\d+$/.test(section)) {
        sectionType = 1;
        sectionValue = Number(section);
      } else if (/^[a-z]$/.test(section)) {
        sectionType = 2;
        sectionValue = section.charCodeAt(0) - 96; // a=1
      } else {
        sectionType = 3;
        sectionValue = 0;
      }

      return { levelRank, grade, sectionType, sectionValue, displayName };
    };

    const compareClasses = (a: any, b: any) => {
      const aKey = parseClassSortKey(a?.displayName ?? a?.name);
      const bKey = parseClassSortKey(b?.displayName ?? b?.name);

      if (aKey.levelRank !== bKey.levelRank) return aKey.levelRank - bKey.levelRank;
      if (aKey.grade !== bKey.grade) return aKey.grade - bKey.grade;
      if (aKey.sectionType !== bKey.sectionType) return aKey.sectionType - bKey.sectionType;
      if (aKey.sectionValue !== bKey.sectionValue) return aKey.sectionValue - bKey.sectionValue;
      return aKey.displayName.localeCompare(bKey.displayName, undefined, { numeric: true, sensitivity: "base" });
    };

    return [...availableClasses].sort(compareClasses);
  }, [availableClasses]);

  // Add step state
  const [currentStep, setCurrentStep] = useState(1);

  const initialFormState: TeacherFormData = {
    schoolId: schoolId,
    employeeId: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    department: "",
    qualification: "",
    dateOfBirth: "",
    joiningDate: "",
    gender: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pinCode: "",
    emergencyContact: "",
    aadharNumber: "",
    panNumber: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    isActive: true,
    role: "",
    experience: "",
    specialization: "",
    status: true,
    enabled: true,
    password: "",
    classIds: [],
    teacherOfClass: [],
  };

  const [formData, setFormData] = useState<TeacherFormData>(initialFormState);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      if (initialData) {
        setFormData({
          ...initialFormState,
          ...initialData,
          schoolId: schoolId,
          password: "",
          // Ensure dates are properly formatted
          dateOfBirth: initialData.dateOfBirth || "",
          joiningDate: initialData.joiningDate || "",
          // Ensure teacherOfClass is an array and filter out empty strings
          teacherOfClass: (initialData.teacherOfClass || []).filter((className: string) => className && className.trim() !== ''),
        });
      } else {
        setFormData(initialFormState);
      }
    }
    setFormErrors({});
    // Reset step when modal opens
    setCurrentStep(1);
  }, [initialData, schoolId, open]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    if (name) {
      let newValue = value;

      // Handle phone number validation
      if (name === "phone") {
        // Only allow digits
        const digitsOnly = value.replace(/\D/g, '');
        
        // Limit to 10 digits maximum
        let validDigits = digitsOnly.slice(0, 10);
        
        // If there's at least one digit, check if first digit is valid (6, 7, 8, or 9)
        if (validDigits.length > 0) {
          const firstDigit = validDigits[0];
          // If first digit is not valid, don't allow it
          if (!['6', '7', '8', '9'].includes(firstDigit)) {
            // Don't update the field if invalid first digit
            return;
          }
        }
        
        // Update form data with valid digits
        setFormData((prev) => ({
          ...prev,
          [name]: validDigits,
        }));
        
        // Validate phone number and set errors
        if (validDigits.length > 0) {
          if (validDigits.length !== 10) {
            setFormErrors((prev) => ({
              ...prev,
              phone: t("phone_must_be_10_digits") || "Phone number must be exactly 10 digits",
            }));
          } else {
            setFormErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors.phone;
              return newErrors;
            });
          }
        } else {
          // Clear error if field is empty (will be caught by required validation)
          setFormErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.phone;
            return newErrors;
          });
        }
        return;
      }

      // Handle emergency contact validation (not required, but if provided must be 10 digits starting with 6,7,8,9)
      if (name === "emergencyContact") {
        // Only allow digits
        const digitsOnly = value.replace(/\D/g, '');
        
        // Limit to 10 digits maximum
        let validDigits = digitsOnly.slice(0, 10);
        
        // If there's at least one digit, check if first digit is valid (6, 7, 8, or 9)
        if (validDigits.length > 0) {
          const firstDigit = validDigits[0];
          // If first digit is not valid, don't allow it
          if (!['6', '7', '8', '9'].includes(firstDigit)) {
            // Don't update the field if invalid first digit
            return;
          }
        }
        
        // Update form data with valid digits
        setFormData((prev) => ({
          ...prev,
          [name]: validDigits,
        }));
        
        // Clear error when user is typing (validation will happen on Next click)
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.emergencyContact;
          return newErrors;
        });
        return;
      }

      // Handle PIN code validation (6 digits only)
      if (name === "pinCode") {
        const cleanValue = value.replace(/\D/g, "").slice(0, 6);

        setFormData((prev) => ({
          ...prev,
          [name]: cleanValue,
        }));

        if (cleanValue.length > 0 && cleanValue.length !== 6) {
          setFormErrors((prev) => ({
            ...prev,
            pinCode: "Please enter a valid 6-digit pincode",
          }));
        } else {
          setFormErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.pinCode;
            return newErrors;
          });
        }
        return;
      }

      // Handle aadhar number validation (12 digits only)
      if (name === "aadharNumber") {
        // Only allow digits
        const digitsOnly = value.replace(/\D/g, '');
        
        // Limit to 12 digits maximum
        let validDigits = digitsOnly.slice(0, 12);
        
        // Update form data with valid digits
        setFormData((prev) => ({
          ...prev,
          [name]: validDigits,
        }));
        
        // Clear error when user is typing (validation will happen on Next click)
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.aadharNumber;
          return newErrors;
        });
        return;
      }

      // Handle PAN number validation (format: ABCDE1234F - 5 letters, 4 digits, 1 letter)
      if (name === "panNumber") {
        // Convert to uppercase and remove spaces
        const upperValue = value.toUpperCase().replace(/\s/g, '');
        
        // Allow only letters and digits
        const alphanumeric = upperValue.replace(/[^A-Z0-9]/g, '');
        
        // Limit to 10 characters maximum
        let validValue = alphanumeric.slice(0, 10);
        
        // Update form data
        setFormData((prev) => ({
          ...prev,
          [name]: validValue,
        }));
        
        // Clear error when user is typing (validation will happen on Next click)
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.panNumber;
          return newErrors;
        });
        return;
      }

      // Handle IFSC code validation (format: ABCD0XXXXXX - 4 letters, 1 zero, 6 alphanumeric)
      if (name === "ifscCode") {
        // Convert to uppercase and allow only letters and digits (remove spaces/special characters)
        const cleanValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        
        // Limit to 11 characters maximum
        let validValue = cleanValue.slice(0, 11);
        
        // Update form data
        setFormData((prev) => ({
          ...prev,
          [name]: validValue,
        }));
        
        // Clear error when user is typing (validation will happen on submit)
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.ifscCode;
          return newErrors;
        });
        return;
      }

      // Handle account number validation (9-18 digits)
      if (name === "accountNumber") {
        // Only allow digits
        const digitsOnly = value.replace(/\D/g, '');
        
        // Limit to 18 digits maximum
        let validDigits = digitsOnly.slice(0, 18);
        
        // Update form data with valid digits
        setFormData((prev) => ({
          ...prev,
          [name]: validDigits,
        }));
        
        // Clear error when user is typing (validation will happen on submit)
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.accountNumber;
          return newErrors;
        });
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }
  };

  const parseISODateToLocal = (dateVal: any): Date | null => {
    if (!dateVal) return null;
    if (dateVal instanceof Date) {
      return isNaN(dateVal.getTime()) ? null : dateVal;
    }
    if (typeof dateVal === "string") {
      if (dateVal.includes("T")) {
        const date = new Date(dateVal);
        return isNaN(date.getTime()) ? null : date;
      }
      const parts = dateVal.split("-");
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // 0-indexed
        const day = parseInt(parts[2], 10);
        const date = new Date(year, month, day);
        return isNaN(date.getTime()) ? null : date;
      }
      const date = new Date(dateVal);
      return isNaN(date.getTime()) ? null : date;
    }
    if (Array.isArray(dateVal) && dateVal.length >= 3) {
      const year = Number(dateVal[0]);
      const month = Number(dateVal[1]) - 1;
      const day = Number(dateVal[2]);
      const date = new Date(year, month, day);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  };

  const handleDateChange = (name: string) => (date: Date | null) => {
    if (date && date instanceof Date && !isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      setFormData((prev) => ({
        ...prev,
        [name]: formattedDate,
      }));
    } else if (date === null) {
      setFormData((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleClassChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    // On autofill we get a stringified value.
    const selectedClassNames = typeof value === 'string' ? value.split(',') : value;
    // Filter out invalid entries and empty strings, trim whitespace
    const filteredClassNames = selectedClassNames
      .filter((name: unknown): name is string => typeof name === "string")
      .map((name: string) => name.trim())
      .filter((name: string) => name !== "");
    setFormData((prev) => ({
      ...prev,
      teacherOfClass: filteredClassNames,
    }));
  };

  const handleClassDelete = (classNameToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      teacherOfClass: (prev.teacherOfClass || []).filter(
        (className: string) => className !== classNameToRemove
      ),
    }));
  };

  const handlePinCodeBeforeInput = (event: React.FormEvent<HTMLInputElement>) => {
    const nativeEvent = event.nativeEvent as InputEvent;
    const data = nativeEvent.data;
    if (!data) return;

    const input = event.currentTarget;
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? input.value.length;
    const nextValue =
      input.value.slice(0, selectionStart) + data + input.value.slice(selectionEnd);

    if (!/^\d{0,6}$/.test(nextValue)) {
      event.preventDefault();
    }
  };

  const handlePinCodePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData("text");
    const input = event.currentTarget;
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? input.value.length;
    const nextValue =
      input.value.slice(0, selectionStart) + pastedText + input.value.slice(selectionEnd);

    if (!/^\d{0,6}$/.test(nextValue)) {
      event.preventDefault();
    }
  };

  const validate = (formData: TeacherFormData) => {
    const errors: { [key: string]: string } = {};
    if (!formData.firstName || formData.firstName.trim() === "") {
      errors.firstName = t("first_name_required");
    }
    if (!formData.lastName || formData.lastName.trim() === "") {
      errors.lastName = t("last_name_required");
    }
    if (!formData.employeeId || formData.employeeId.trim() === "") {
      errors.employeeId = t("employee_id_required");
    }
    if (!formData.email || formData.email.trim() === "") {
      errors.email = t("email_required_validation");
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      errors.email = t("invalid_email_address");
    }
    if (!formData.department || formData.department.trim() === "") {
      errors.department = t("department_subject_required");
    }
    // Only validate password for new teachers, not for edit
    if (!isEdit && (!formData.password || formData.password.trim() === "")) {
      errors.password = t("password_required");
    }
    if (!formData.pinCode || !/^\d{6}$/.test(formData.pinCode.trim())) {
      errors.pinCode = "Please enter a valid 6-digit pincode";
    }
    // Add more field validations as needed
    return errors;
  };

  // Validate IFSC code format
  const validateIFSC = (ifscCode: string) => {
    if (!ifscCode || ifscCode.trim() === "") {
      return t("ifsc_code_required") || "IFSC code is required";
    }
    
    const ifsc = ifscCode.trim().toUpperCase();
    
    // Check total length
    if (ifsc.length !== 11) {
      return "IFSC code must be exactly 11 characters";
    }
    
    // Check format: First 4 letters, 5th is '0', last 6 alphanumeric
    const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscPattern.test(ifsc)) {
      return "IFSC code must be in format ABCD0XXXXXX (4 letters, 1 zero, 6 alphanumeric)";
    }
    
    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate(formData);
    
    // Validate IFSC code format
    const ifscError = validateIFSC(formData.ifscCode);
    if (ifscError) {
      errors.ifscCode = ifscError;
    }
    
    // Validate account number (9-18 digits)
    if (!formData.accountNumber || formData.accountNumber.trim() === "") {
      errors.accountNumber = t("account_number_required") || "Account number is required";
    } else {
      const accountNumber = formData.accountNumber.trim();
      const digitsOnly = accountNumber.replace(/\D/g, '');
      if (digitsOnly.length < 9 || digitsOnly.length > 18) {
        errors.accountNumber = "Account number must be between 9 and 18 digits";
      }
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const sessionId = selectedSession?.id;

    // Filter out empty strings from teacherOfClass before submitting
    // Always set status to true for both create and update
    const cleanedFormData = {
      ...formData,
      status: true,
      teacherOfClass: (formData.teacherOfClass || []).filter((className: string) => className && className.trim() !== ''),
    };

    if (isEdit) {
      // For editing, remove password field completely and include the id
      const { password, ...formDataWithoutPassword } = cleanedFormData;
      onSubmit({ ...formDataWithoutPassword, schoolId, sessionId });
    } else {
      // For creating new teacher, remove the id field completely
      const { id, ...formDataWithoutId } = cleanedFormData;
      onSubmit({ ...formDataWithoutId, schoolId, sessionId });
    }
  };

  // Step validation functions
  const isStep1Valid = () => {
    const step1Fields = [
      "firstName",
      "lastName",
      "employeeId",
      "gender",
      "dateOfBirth",
      "joiningDate",
      "address",
      "city",
      "state",
      "country",
      "pinCode",
      "phone",
      "email",
      "department",
    ];
    
    // Add password field only for new teachers
    if (!isEdit) {
      step1Fields.push("password");
    }
    
    return (
      step1Fields.every((field) => {
        const value = (formData as any)[field];
        return value && value.toString().trim() !== "";
      })
    );
  };

  // Validate step 1 fields including format validations
  const validateStep1Fields = () => {
    const errors: { [key: string]: string } = {};
    
    // Validate emergency contact (not required, but if provided must be valid)
    if (formData.emergencyContact && formData.emergencyContact.trim() !== "") {
      const emergencyContact = formData.emergencyContact.trim();
      if (emergencyContact.length !== 10) {
        errors.emergencyContact = "Emergency contact must be exactly 10 digits";
      } else if (!['6', '7', '8', '9'].includes(emergencyContact[0])) {
        errors.emergencyContact = "Emergency contact must start with 6, 7, 8, or 9";
      }
    }

    // Validate PIN code (required, must be 6 digits)
    if (!formData.pinCode || !/^\d{6}$/.test(formData.pinCode.trim())) {
      errors.pinCode = "Please enter a valid 6-digit pincode";
    }
    
    // Validate aadhar number (required, must be 12 digits)
    if (!formData.aadharNumber || formData.aadharNumber.trim() === "") {
      errors.aadharNumber = t("aadhar_number_required") || "Aadhar number is required";
    } else {
      const aadharNumber = formData.aadharNumber.trim();
      if (aadharNumber.length !== 12 || !/^\d+$/.test(aadharNumber)) {
        errors.aadharNumber = "Aadhar number must be exactly 12 digits";
      }
    }
    
    // Validate PAN number (required, format: ABCDE1234F)
    if (!formData.panNumber || formData.panNumber.trim() === "") {
      errors.panNumber = t("pan_number_required") || "PAN number is required";
    } else {
      const panNumber = formData.panNumber.trim().toUpperCase();
      const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (!panPattern.test(panNumber)) {
        errors.panNumber = "PAN number must be in format ABCDE1234F (5 letters, 4 digits, 1 letter)";
      }
    }
    
    return errors;
  };

  const isStep2Valid = () => {
    const step2Fields = [
      "bankName",
      "accountNumber",
    ];
    return (
      step2Fields.every((field) => {
        const value = (formData as any)[field];
        return value && value.toString().trim() !== "";
      })
    );
  };

  const handleNext = () => {
    if (currentStep === 1) {
      // Validate required fields
      if (!isStep1Valid()) {
        return;
      }
      
      // Validate format fields (emergency contact, aadhar, pan)
      const formatErrors = validateStep1Fields();
      if (Object.keys(formatErrors).length > 0) {
        setFormErrors((prev) => ({
          ...prev,
          ...formatErrors,
        }));
        return;
      }
      
      // Clear any format errors if validation passes
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.emergencyContact;
        delete newErrors.aadharNumber;
        delete newErrors.panNumber;
        delete newErrors.pinCode;
        return newErrors;
      });
      
      setCurrentStep(2);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  // Step content components
  const renderStep1 = () => (
    <Box sx={{ height: "330px", overflowY: "auto" }}>
      <Grid container spacing={1.2}>
        {/* Row 1 */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("first_name")}
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            margin="dense"
            size="small"
            variant="outlined"
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("last_name")}
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            margin="dense"
            size="small"
            variant="outlined"
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("employee_id")}
            name="employeeId"
            value={formData.employeeId}
            onChange={handleChange}
            required
            margin="dense"
            size="small"
            variant="outlined"
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>

        {/* Row 2 */}
        <Grid item xs={12} md={4}>
          <FormControl
            fullWidth
            margin="dense"
            required
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          >
            <InputLabel
              required
              sx={{
                "& .MuiFormLabel-asterisk": { color: "red" },
              }}
            >
              {t("gender")}
            </InputLabel>
            <Select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              label={t("gender")}
              size="small"
              required
              input={<OutlinedInput label={t("gender")} size="small" />}
              displayEmpty
              sx={{
                minHeight: "40px",
                display: "flex",
                alignItems: "center",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "8px",
                  paddingBottom: "8px",
                },
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}
              renderValue={(selected) =>
                !selected ? (
                  <span style={{ color: "#aaa" }}></span>
                ) : (
                  <span style={{ color: "black" }}>{selected}</span>
                )
              }
            >
              <MenuItem value="Male">{t("male")}</MenuItem>
              <MenuItem value="Female">{t("female")}</MenuItem>
              <MenuItem value="Other">{t("other")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={
                <span>
                  {t("date_of_birth")} <span style={{ color: "red" }}>*</span>
                </span>
              }
              value={parseISODateToLocal(formData.dateOfBirth)}
              onChange={handleDateChange("dateOfBirth")}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "dense",
                  size: "small",
                  variant: "outlined",
                  sx: { backgroundColor: "#f8f9fb" },
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={
                <span>
                  {t("joining_date")} <span style={{ color: "red" }}>*</span>
                </span>
              }
              value={parseISODateToLocal(formData.joiningDate)}
              onChange={handleDateChange("joiningDate")}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "dense",
                  size: "small",
                  variant: "outlined",
                  sx: { backgroundColor: "#f8f9fb" },
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        {/* Row 3 - Address */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("address")}
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
            margin="dense"
            size="small"
            variant="outlined"
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>

        {/* Row 4 */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("city")}
            name="city"
            required
            value={formData.city}
            onChange={handleChange}
            margin="dense"
            size="small"
            variant="outlined"
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("state")}
            name="state"
            required
            value={formData.state}
            onChange={handleChange}
            margin="dense"
            size="small"
            variant="outlined"
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("country")}
            name="country"
            required
            value={formData.country}
            onChange={handleChange}
            margin="dense"
            size="small"
            variant="outlined"
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>

        {/* Row 5 */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("pin_code")}
            name="pinCode"
            required
            value={formData.pinCode}
            onChange={handleChange}
            margin="dense"
            size="small"
            variant="outlined"
            error={!!formErrors.pinCode}
            helperText={formErrors.pinCode}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 6,
              onBeforeInput: handlePinCodeBeforeInput,
              onPaste: handlePinCodePaste,
            }}
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("phone")}
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            margin="dense"
            size="small"
            variant="outlined"
            error={!!formErrors.phone}
            helperText={formErrors.phone}
            inputProps={{
              maxLength: 10,
              inputMode: "numeric",
              pattern: "[6-9][0-9]{9}",
            }}
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("email")}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            margin="dense"
            size="small"
            variant="outlined"
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>

        {/* Row 6 - Password field only shown for new teachers */}
        {!isEdit && (
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t("password")}
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              required
              margin="dense"
              size="small"
              variant="outlined"
              autoComplete="new-password"
              InputLabelProps={{
                sx: {
                  "& .MuiFormLabel-asterisk": { color: "red" },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "#f8f9fb",
              }}
            />
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("department")}
            name="department"
            value={formData.department}
            onChange={handleChange}
            margin="dense"
            size="small"
            variant="outlined"
            required
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>

        {/* Row 7 */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("emergency_contact")}
            name="emergencyContact"
            value={formData.emergencyContact}
            onChange={handleChange}
            margin="dense"
            size="small"
            variant="outlined"
            error={!!formErrors.emergencyContact}
            helperText={formErrors.emergencyContact}
            inputProps={{
              maxLength: 10,
              inputMode: "numeric",
              pattern: "[6-9][0-9]{9}",
            }}
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("aadhar_number")}
            name="aadharNumber"
            required
            value={formData.aadharNumber}
            onChange={handleChange}
            margin="dense"
            size="small"
            variant="outlined"
            error={!!formErrors.aadharNumber}
            helperText={formErrors.aadharNumber}
            inputProps={{
              maxLength: 12,
              inputMode: "numeric",
              pattern: "[0-9]{12}",
            }}
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>

        {/* Row 8 */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("pan_number")}
            name="panNumber"
            required
            value={formData.panNumber}
            onChange={handleChange}
            margin="dense"
            size="small"
            variant="outlined"
            error={!!formErrors.panNumber}
            helperText={formErrors.panNumber}
            inputProps={{
              maxLength: 10,
              style: { textTransform: "uppercase" },
            }}
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>
        {/* Row 9 - Teacher of Classes */}
        <Grid item xs={12} md={4}>
          <FormControl
            fullWidth
            margin="dense"
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          >
            <InputLabel
              sx={{
                "& .MuiFormLabel-asterisk": { color: "red" },
              }}
            >
              {t("teacher_of_classes") || "Teacher of Classes"}
            </InputLabel>
            <Select
              multiple
              open={isClassDropdownOpen}
              onOpen={() => setIsClassDropdownOpen(true)}
              onClose={() => setIsClassDropdownOpen(false)}
              value={formData.teacherOfClass || []}
              onChange={handleClassChange}
              label={t("teacher_of_classes") || "Teacher of Classes"}
              size="small"
              input={<OutlinedInput label={t("teacher_of_classes") || "Teacher of Classes"} size="small" />}
              sx={{
                minHeight: "40px",
                display: "flex",
                alignItems: "center",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "8px",
                  paddingBottom: "8px",
                  "&:empty": {
                    "&::before": {
                      display: "none",
                    },
                  },
                },
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}
              renderValue={(selected) => {
                const selectedArray = Array.isArray(selected) ? selected : [];
                if (selectedArray.length === 0) {
                  return '';
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedArray.map((className) => (
                      <Chip
                        key={className}
                        label={className}
                        size="small"
                        onDelete={() => handleClassDelete(className)}
                        onMouseDown={(event) => event.stopPropagation()}
                      />
                    ))}
                  </Box>
                );
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              {sortedAvailableClasses.map((classItem) => {
                const displayName = classItem.displayName || (classItem.section 
                  ? `${classItem.name} ${classItem.section}` 
                  : classItem.name);
                const isSelected = (formData.teacherOfClass || []).includes(displayName);
                return (
                  <MenuItem key={classItem.id} value={displayName}>
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box component="span">{displayName}</Box>
                      {isSelected && (
                        <CloseIcon
                          fontSize="small"
                          sx={{
                            color: "#d32f2f",
                            "&:hover": { color: "#b71c1c" },
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleClassDelete(displayName);
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
              <ListSubheader
                onClick={(event) => {
                  event.stopPropagation();
                }}
                sx={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 1,
                  backgroundColor: "#fff",
                  borderTop: "1px solid #e0e0e0",
                  p: 0,
                }}
              >
                <Button
                  fullWidth
                  variant="text"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsClassDropdownOpen(false);
                  }}
                  sx={{
                    borderRadius: 0,
                    py: 1,
                    fontWeight: 600,
                    color: "#3092e3",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  {t("done") || "Done"}
                </Button>
              </ListSubheader>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  const renderStep2 = () => (
    <Box sx={{ height: "330px", overflowY: "auto" }}>
      <Grid container spacing={1.2}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("bank_name")}
            name="bankName"
            required
            value={formData.bankName}
            onChange={handleChange}
            margin="dense"
            size="small"
            variant="outlined"
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("account_number")}
            name="accountNumber"
            required
            value={formData.accountNumber}
            onChange={handleChange}
            margin="dense"
            size="small"
            variant="outlined"
            error={!!formErrors.accountNumber}
            helperText={formErrors.accountNumber}
            inputProps={{
              maxLength: 18,
              inputMode: "numeric",
            }}
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t("ifsc_code")}
            name="ifscCode"
            required
            value={formData.ifscCode}
            onChange={handleChange}
            margin="dense"
            size="small"
            variant="outlined"
            error={!!formErrors.ifscCode}
            helperText={formErrors.ifscCode}
            inputProps={{
              maxLength: 11,
              style: { textTransform: "uppercase" },
            }}
            InputLabelProps={{
              sx: {
                "& .MuiFormLabel-asterisk": { color: "red" },
              },
            }}
            sx={{
              backgroundColor: "#f8f9fb",
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const steps = [
    { label: t("personal_details"), content: renderStep1() },
    { label: t("bank_details"), content: renderStep2() },
  ];

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={isEdit ? t("edit_teacher") : t("add_new_teacher")}
      maxWidth="lg"
      disableEscapeKeyDown
      customActions={
        <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            {currentStep > 1 && (
              <Button
                onClick={handlePrevious}
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                sx={{
                  minWidth: 100,
                  borderColor: "#666",
                  color: "#666",
                  "&:hover": { borderColor: "#333", color: "#333", backgroundColor: "rgba(102,102,102,0.08)" },
                }}
              >
                {t("previous")}
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            {currentStep < 2 && (
              <Button
                onClick={handleNext}
                variant="contained"
                endIcon={<NavigateNextIcon />}
                disabled={!isStep1Valid()}
                sx={{
                  minWidth: 100,
                  backgroundColor: "#3092e3",
                  "&:hover": { backgroundColor: "primary.main" },
                  "&.Mui-disabled": { backgroundColor: "#b2ebff", opacity: 0.6 },
                }}
              >
                {t("next")}
              </Button>
            )}
            {currentStep === 2 && (
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={!isStep2Valid()}
                sx={{
                  minWidth: 100,
                  backgroundColor: "#3092e3",
                  "&:hover": { backgroundColor: "primary.main" },
                  "&.Mui-disabled": { backgroundColor: "#b2ebff", opacity: 0.6 },
                }}
              >
                {isEdit ? t("update") : t("create")}
              </Button>
            )}
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderColor: "#f44336",
                color: "#f44336",
                "&.Mui-disabled": { borderColor: "#b2ebff", color: "#b2ebff", backgroundColor: "transparent", opacity: 1 },
              }}
            >
              {t("cancel")}
            </Button>
          </Box>
        </Box>
      }
    >
      {/* Stepper */}
      <Box>
        <Stepper
          activeStep={currentStep - 1}
          alternativeLabel
          sx={{
            "& .MuiStepLabel-root": {
              "& .MuiStepLabel-labelContainer": {
                marginTop: "-10px",
                marginBottom: "20px",
              },
            },
          }}
        >
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Step Content */}
      {steps[currentStep - 1].content}
    </CommonModal>
  );
};

export default TeacherFormModal;
